import { z } from 'zod';

import { SETUP_STORE_COLOR_PRESETS } from '@/components/setup/setupStoreColors';
import { brandingConfig } from '@/config/branding';
import currencyData from '@/data/currencies.json';

/** Full ISO currency list — same source the standalone store-create page offers. */
export const SETUP_MANUAL_CURRENCIES = Object.keys(currencyData) as [string, ...string[]];

const hexColorSchema = z
  .string()
  .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color');

export const setupManualStoreSchema = z.object({
  name: z.string().min(1, 'Store name is required'),
  currency: z.enum(SETUP_MANUAL_CURRENCIES),
  primaryColor: hexColorSchema,
  logoUrl: z.string(),
  /** Digital stores skip shipping entirely (no address, no rates). */
  digitalProduct: z.boolean(),
});

export type SetupManualStoreFormData = z.infer<typeof setupManualStoreSchema>;

export interface SetupManualProductSchemaMessages {
  productNameRequired: string;
  variantNameRequired: string;
  imageRequired: string;
  priceRequired: string;
  priceInvalid: string;
  variantsMin: string;
}

function parsePriceString(value: string): number | null {
  const trimmed = value.trim().replace(',', '.');
  if (!trimmed) return null;
  const amount = Number.parseFloat(trimmed);
  return Number.isFinite(amount) ? amount : null;
}

export function createSetupManualProductSchema(messages: SetupManualProductSchemaMessages) {
  const variantSchema = z.object({
    name: z.string().trim().min(1, messages.variantNameRequired),
    imageUrl: z.string().trim().min(1, messages.imageRequired),
    sku: z.string().optional(),
    priceAmount: z
      .string()
      .trim()
      .min(1, messages.priceRequired)
      .refine((value) => {
        const amount = parsePriceString(value);
        return amount !== null && amount > 0;
      }, messages.priceInvalid),
  });

  return z.object({
    /** Set when the block was loaded from an existing product — edits recreate it on submit. */
    productId: z.string().optional(),
    name: z.string().trim().min(1, messages.productNameRequired),
    description: z.string().optional(),
    variants: z.array(variantSchema).min(1, messages.variantsMin),
  });
}

export type SetupManualProductFormData = z.infer<ReturnType<typeof createSetupManualProductSchema>>;

/** Untouched product block — every text field blank. The step skips submit entirely when products already exist and all blocks are untouched. */
export function isEmptySetupProductDraft(product: {
  productId?: string;
  name?: string;
  description?: string;
  variants?: Array<{ name?: string; imageUrl?: string; sku?: string; priceAmount?: string }>;
}): boolean {
  const hasText = (value?: string) => Boolean(value?.trim());

  if (product.productId) return false;
  if (hasText(product.name) || hasText(product.description)) return false;
  return (product.variants ?? []).every(
    (variant) =>
      !hasText(variant?.name) &&
      !hasText(variant?.imageUrl) &&
      !hasText(variant?.sku) &&
      !hasText(variant?.priceAmount),
  );
}

/** Value-equality between a form block and its last saved version — decides whether an edit must be persisted. */
export function isSameSetupProductDraft(
  a: SetupManualProductFormData,
  b: SetupManualProductFormData,
): boolean {
  const text = (value?: string) => value?.trim() ?? '';
  const price = (value?: string) => (value ? parsePriceString(value) : null);

  if (text(a.name) !== text(b.name) || text(a.description) !== text(b.description)) return false;
  if (a.variants.length !== b.variants.length) return false;

  return a.variants.every((variant, index) => {
    const other = b.variants[index];
    return (
      text(variant.name) === text(other.name) &&
      text(variant.imageUrl) === text(other.imageUrl) &&
      text(variant.sku) === text(other.sku) &&
      price(variant.priceAmount) === price(other.priceAmount)
    );
  });
}

/** Setup collects several products in one form — submitted in a single pass. */
export function createSetupManualProductsSchema(messages: SetupManualProductSchemaMessages) {
  return z.object({ products: z.array(createSetupManualProductSchema(messages)).min(1) });
}

export type SetupManualProductsFormData = z.infer<ReturnType<typeof createSetupManualProductsSchema>>;

export function createSetupManualProductDefaults(): SetupManualProductFormData {
  return {
    name: '',
    description: '',
    variants: [{ name: '', imageUrl: '', sku: '', priceAmount: '' }],
  };
}

export function createSetupManualProductsDefaults(): SetupManualProductsFormData {
  return { products: [createSetupManualProductDefaults()] };
}

export const setupManualStoreDefaults: SetupManualStoreFormData = {
  name: '',
  currency: 'USD',
  primaryColor: SETUP_STORE_COLOR_PRESETS[0] ?? brandingConfig.primaryColor,
  logoUrl: '',
  digitalProduct: false,
};

export { parsePriceString };
